import { Grid } from '../../core/Grid.js';
import { KnightGame } from './KnightGame.js';
import { KnightVortex } from './KnightVortex.js';
import { Position, positionKey, parsePositionKey } from '../../core/GridGeometry.js';
import { GridRenderer } from '../../rendering/GridRenderer.js';
import { RenderConfig, COLORS, colorWithAlpha } from '../../rendering/Renderer.js';
import { isVortexGame } from '../../games/IVortexGame.js';
import { TweenScheduler, MOTION, staggerDelay } from '../../rendering/juice.js';

export interface KnightRenderConfig extends Partial<RenderConfig> {
    pathColor?: string;
    pathWidth?: number;
    dotRadius?: number;
    accessibleColor?: string;
    backtrackColor?: string;
    selectedColor?: string;
    nextDigitHighlight?: string;
}

const KNIGHT_DEFAULTS = {
    pathColor: COLORS.teal,
    pathWidth: 3,
    dotRadius: 6,
    accessibleColor: colorWithAlpha('teal', 0.3),
    backtrackColor: colorWithAlpha('amber', 0.3),
    selectedColor: COLORS.teal,
    nextDigitHighlight: colorWithAlpha('amber', 0.3)
};

/**
 * The cells a set of segments touches.
 */
function invalidCells(segments: Set<string>): Set<string> {
    const cells = new Set<string>();
    for (const seg of segments) {
        for (const pos of parseSegmentKey(seg)) cells.add(positionKey(pos));
    }
    return cells;
}

/**
 * Parse a segment key back into two positions.
 */
function parseSegmentKey(key: string): [Position, Position] {
    const [keyA, keyB] = key.split('-');
    const [rowA, colA] = keyA.split(',').map(Number);
    const [rowB, colB] = keyB.split(',').map(Number);
    return [
        { row: rowA, col: colA },
        { row: rowB, col: colB }
    ];
}

type SegOrigin = 'a' | 'b' | 'mid';

interface SegTween {
    kind: 'enter' | 'exit';
    /** Clock time the tween begins — in the future while a stagger delay runs. */
    start: number;
    origin: SegOrigin;
    /** Exit only: the colour it had on the last frame it existed. */
    color: string;
}

export class KnightRenderer extends GridRenderer {
    private knightConfig: typeof KNIGHT_DEFAULTS;
    private gateMode: boolean = false;

    // Animation state, all renderer-local (docs/juice.md)
    private tweens = new TweenScheduler();
    private segTweens = new Map<string, SegTween>();
    private segState = new Set<string>();               // what the last frame drew
    private segColors = new Map<string, string>();      // last colour, for removals
    private segsSynced: boolean = false;                // first frame seeds, never animates
    private reached = new Set<number>();                // digits the path had reached
    private ringPops = new Map<number, number>();       // digit -> tween start
    private reachedSynced: boolean = false;
    /** Set by any tween still running this frame; drives the self-scheduled rAF. */
    private animating: boolean = false;
    private lastGame: KnightGame | null = null;

    constructor(canvas: HTMLCanvasElement, grid: Grid, config: KnightRenderConfig = {}) {
        super(canvas, grid, config);
        this.knightConfig = { ...KNIGHT_DEFAULTS, ...config };
    }

    setGateMode(enabled: boolean): void {
        this.gateMode = enabled;
    }

    /** Disable tweening (see `TweenScheduler`). Drops anything in flight, so the
     *  next render draws every segment and ring at rest. */
    setSelfAnimation(enabled: boolean): void {
        this.tweens.setEnabled(enabled);
        if (enabled) return;
        this.segTweens.clear();
        this.ringPops.clear();
    }

    render(game: KnightGame): void {
        const grid = game.getGrid();
        const digitConnected = this.getDigitConnectedCells(game);
        const invalid = game.getInvalidSegments();

        this.lastGame = game;
        this.animating = false;
        this.syncSegmentTweens(game);
        this.syncRingPops(game.getDigitsReached());

        this.clear();
        this.drawGrid();

        if (this.gateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }

        this.highlightNextDigit(game);
        this.drawAccessibleCells(game);
        if (isVortexGame(game)) {
            this.drawActiveVortexGates(game);
        }
        this.drawTeleportLinks(game);
        this.highlightSelectedCell(game);
        this.drawSegments(game, digitConnected, undefined, invalid);
        this.drawPathDots(game, grid, digitConnected, invalidCells(invalid));
        this.drawReachedDigitCircles(game, grid);
        this.drawDigits(grid);
        this.drawKeyboardCursor();

        if (this.animating) {
            this.tweens.request(() => {
                if (this.lastGame) this.render(this.lastGame);
            });
        }
    }

    // ── Segment and ring animation (docs/juice.md) ────────────────────
    //
    // Everything here resolves toward the cell the player clicked. A new segment
    // grows from its far end toward that cell; a backtracked one retracts into
    // it. The board tells us which end that is: after either action the game
    // leaves the clicked cell selected, so the segment's *other* end is where the
    // motion starts. Away from a click — a restore, a right-click clearing a
    // whole cell — it falls back to growing out of whatever chain is already
    // there, and to the midpoint when there is none.

    /**
     * Diff the segments against the last frame and start a tween per change. A bulk
     * change (Reset, Restore best) fans out in reading order.
     */
    private syncSegmentTweens(game: KnightGame): void {
        const segments = game.getSegments();
        if (!this.tweens.isEnabled()) {
            this.segTweens.clear();
            this.segState = new Set(segments);
            return;
        }
        // First frame on this board: adopt it as-is, or opening a grid with a saved
        // solution would redraw the whole path.
        if (!this.segsSynced) {
            this.segState = new Set(segments);
            this.segsSynced = true;
            return;
        }

        const added: string[] = [];
        const removed: string[] = [];
        for (const key of segments) if (!this.segState.has(key)) added.push(key);
        for (const key of this.segState) if (!segments.has(key)) removed.push(key);
        if (added.length === 0 && removed.length === 0) return;

        const selected = game.getSelectedCell();
        const selectedKey = selected ? positionKey(selected) : null;
        const rank = (key: string) => {
            const [a] = parseSegmentKey(key);
            return a.row * this.grid.size + a.col;
        };
        const changed = [...added, ...removed].sort((x, y) => rank(x) - rank(y));

        const now = this.tweens.now();
        changed.forEach((key, i) => {
            const start = now + staggerDelay(i, changed.length);
            const entering = segments.has(key);
            // Both directions ask the same question of the board *without* this
            // segment: for a placement, the path as it stood before; for a removal,
            // the path as it stands after.
            const origin = this.segOrigin(key, entering ? this.segState : segments, selectedKey, entering);
            if (entering) {
                this.segTweens.set(key, { kind: 'enter', start, origin, color: '' });
            } else {
                this.segTweens.set(key, {
                    kind: 'exit', start, origin,
                    color: this.segColors.get(key) ?? this.knightConfig.pathColor
                });
            }
        });

        this.segState = new Set(segments);
    }

    /**
     * Which end a segment grows from (or retracts toward). The clicked cell wins:
     * a new segment grows *away* from it and a removed one pulls *into* it, so both
     * motions finish where the player's attention already is. With no click to go
     * on, the end that already carries path decides, and failing that the midpoint.
     */
    private segOrigin(
        segKey: string, others: Set<string>, selectedKey: string | null, entering: boolean
    ): SegOrigin {
        const [a, b] = parseSegmentKey(segKey);
        const ka = positionKey(a), kb = positionKey(b);

        if (selectedKey === ka) return entering ? 'b' : 'a';
        if (selectedKey === kb) return entering ? 'a' : 'b';

        let atA = false, atB = false;
        for (const other of others) {
            if (other === segKey) continue;
            const [oa, ob] = parseSegmentKey(other);
            const koa = positionKey(oa), kob = positionKey(ob);
            if (koa === ka || kob === ka) atA = true;
            if (koa === kb || kob === kb) atB = true;
            if (atA && atB) break;
        }
        if (atA === atB) return 'mid';
        return atA ? 'a' : 'b';
    }

    /** How much of a segment is on the board this frame, and from which end. */
    private segExtent(
        tween: SegTween, now: number
    ): { origin: SegOrigin; grown: number; done: boolean } {
        const { value, done } = this.tweens.tween(tween.start, MOTION[tween.kind], now);
        // The `exit` curve runs the *disappearance*, so invert it.
        return { origin: tween.origin, grown: tween.kind === 'enter' ? value : 1 - value, done };
    }

    /**
     * Note the digits the path has just reached, so their rings can be popped.
     * Only newly reached ones: a digit dropping back off the path simply reverts.
     */
    private syncRingPops(reached: number[]): void {
        const next = new Set(reached);
        if (!this.tweens.isEnabled()) {
            this.ringPops.clear();
            this.reached = next;
            return;
        }
        if (!this.reachedSynced) {
            this.reached = next;
            this.reachedSynced = true;
            return;
        }

        const now = this.tweens.now();
        for (const digit of next) {
            if (!this.reached.has(digit)) this.ringPops.set(digit, now);
        }
        for (const digit of this.reached) {
            if (!next.has(digit)) this.ringPops.delete(digit);
        }
        this.reached = next;
    }

    /** How big a just-reached digit's ring reads this frame — 1 unless still landing. */
    private ringPopScale(digit: number, now: number): number {
        const start = this.ringPops.get(digit);
        if (start === undefined) return 1;
        const { value, done } = this.tweens.tween(start, MOTION.emphasis, now);
        if (done) {
            this.ringPops.delete(digit);
            return 1;
        }
        this.animating = true;
        return value;
    }

    /**
     * Ring each digit the path has reached: a background-filled teal circle sized
     * between the digit glyph and its cell, drawn over the path so the digit reads
     * cleanly on top.
     */
    private drawReachedDigitCircles(game: KnightGame, grid: Grid): void {
        const radius = this.config.cellSize * 0.32;
        const now = this.tweens.now();

        for (const digit of game.getDigitsReached()) {
            const pos = grid.getDigitPosition(digit);
            if (!pos) continue;

            // Reaching a digit is what the game is *for*, so the ring lands with a
            // swing (`emphasis`). It closes over the path beneath it, so it grows
            // from full opacity rather than fading — nothing flickers under it.
            const center = this.getCellCenter(pos);
            this.ctx.beginPath();
            this.ctx.arc(center.x, center.y, radius * this.ringPopScale(digit, now), 0, Math.PI * 2);
            this.ctx.fillStyle = COLORS.background;
            this.ctx.fill();
            this.ctx.lineWidth = 2.5;
            this.ctx.strokeStyle = this.knightConfig.pathColor;
            this.ctx.stroke();
        }
    }

    /**
     * Compute which cells are in connected components that contain at least one digit.
     */
    private getDigitConnectedCells(game: KnightGame): Set<string> {
        const segments = game.getSegments();
        const grid = game.getGrid();

        // Build adjacency map
        const adjacency = new Map<string, string[]>();
        for (const seg of segments) {
            const [a, b] = parseSegmentKey(seg);
            const keyA = positionKey(a);
            const keyB = positionKey(b);
            if (!adjacency.has(keyA)) adjacency.set(keyA, []);
            if (!adjacency.has(keyB)) adjacency.set(keyB, []);
            adjacency.get(keyA)!.push(keyB);
            adjacency.get(keyB)!.push(keyA);
        }

        // Include teleport links in adjacency for connected component analysis
        if (game instanceof KnightVortex) {
            for (const [entryKey, exitKey] of game.getTeleportedPairs()) {
                if (!adjacency.has(entryKey)) adjacency.set(entryKey, []);
                if (!adjacency.has(exitKey)) adjacency.set(exitKey, []);
                adjacency.get(entryKey)!.push(exitKey);
                adjacency.get(exitKey)!.push(entryKey);
            }
        }

        // Find connected components via BFS, check for digits
        const visited = new Set<string>();
        const result = new Set<string>();

        for (const startKey of adjacency.keys()) {
            if (visited.has(startKey)) continue;

            const component: string[] = [];
            const queue = [startKey];
            let hasDigit = false;

            while (queue.length > 0) {
                const key = queue.shift()!;
                if (visited.has(key)) continue;
                visited.add(key);
                component.push(key);

                const [row, col] = key.split(',').map(Number);
                if (grid.getDigitAt({ row, col }) !== null) {
                    hasDigit = true;
                }

                for (const neighbor of (adjacency.get(key) || [])) {
                    if (!visited.has(neighbor)) {
                        queue.push(neighbor);
                    }
                }
            }

            if (hasDigit) {
                for (const key of component) {
                    result.add(key);
                }
            }
        }

        return result;
    }

    private highlightNextDigit(game: KnightGame): void {
        const selected = game.getSelectedCell();
        if (!selected) return;

        // Only highlight if selected cell is an endpoint (can extend)
        const connections = game.getConnectedCells(selected).length;
        if (connections >= 2) return;

        // Get all valid next digits for this endpoint
        const nextDigits = game.getNextDigitsForEndpoint(selected);
        if (nextDigits.length === 0) return;

        for (const nextDigit of nextDigits) {
            const pos = game.getGrid().getDigitPosition(nextDigit);
            if (!pos) continue;

            this.drawCellBorder(pos, COLORS.amber);
        }
    }

    private highlightSelectedCell(game: KnightGame): void {
        const selected = game.getSelectedCell();
        if (!selected) return;

        this.drawCellBorder(selected, this.knightConfig.selectedColor);

        // If selected cell is a teleport exit, also highlight the entry gate
        if (game instanceof KnightVortex) {
            const teleportedPairs = game.getTeleportedPairs();
            for (const [entryKey, exitKey] of teleportedPairs) {
                if (exitKey === positionKey(selected)) {
                    this.drawCellBorder(parsePositionKey(entryKey), this.knightConfig.selectedColor);
                    break;
                }
            }
        }
    }

    private drawAccessibleCells(game: KnightGame): void {
        const selected = game.getSelectedCell();
        if (!selected) return;

        const accessible = game.getAccessibleCells();
        const connectedCells = game.getConnectedCells(selected);
        const connectedKeys = new Set(connectedCells.map(positionKey));

        for (const pos of accessible) {
            const isBacktrack = connectedKeys.has(positionKey(pos));

            if (isBacktrack) {
                // Backtrack targets in amber
                this.highlightCell(pos, this.knightConfig.backtrackColor);
            } else {
                // New moves in teal
                this.highlightCell(pos, this.knightConfig.accessibleColor);
            }
        }
    }

    /** Scoring overlay: redraw the knight path (primary, the steps) in amber. */
    protected drawScoringPrimary(game: KnightGame): void {
        this.drawSegments(game, new Set<string>(), COLORS.amber);
    }

    private drawSegments(
        game: KnightGame,
        digitConnected: Set<string>,
        colorOverride?: string,
        invalid: Set<string> = new Set()
    ): void {
        const segments = game.getSegments();

        // The scoring overlay redraws the path in a flat colour on a frozen board:
        // it reads state, it doesn't advance it. (The diff already ran, in render.)
        const live = colorOverride === undefined;
        const now = this.tweens.now();

        this.ctx.lineWidth = this.knightConfig.pathWidth;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        for (const seg of segments) {
            const [a, b] = parseSegmentKey(seg);
            const keyA = positionKey(a);
            const isDigitConnected = digitConnected.has(keyA) && !invalid.has(seg);
            const color = colorOverride ?? (isDigitConnected ? this.knightConfig.pathColor : COLORS.amber);
            if (live) this.segColors.set(seg, color);

            const tween = live ? this.segTweens.get(seg) : undefined;
            const shape = tween
                ? this.segExtent(tween, now)
                : { origin: 'mid' as SegOrigin, grown: 1, done: true };
            if (tween && shape.done) this.segTweens.delete(seg);
            else if (tween) this.animating = true;

            this.ctx.strokeStyle = color;
            this.strokeSegment(a, b, shape.origin, shape.grown);
        }

        // Segments being removed are already gone from game state — they only exist
        // here, pulling back in the colour they wore when they went.
        for (const [seg, tween] of live ? this.segTweens : []) {
            if (tween.kind !== 'exit' || segments.has(seg)) continue;
            const shape = this.segExtent(tween, now);
            if (shape.done) {
                this.segTweens.delete(seg);
                this.segColors.delete(seg);
                continue;
            }
            this.animating = true;
            const [a, b] = parseSegmentKey(seg);
            this.ctx.strokeStyle = tween.color;
            this.strokeSegment(a, b, shape.origin, shape.grown);
        }
    }

    /** Draw the `grown` fraction of a knight move, extending from `origin`. */
    private strokeSegment(a: Position, b: Position, origin: SegOrigin, grown: number): void {
        if (grown <= 0) return;
        const ca = this.getCellCenter(a);
        const cb = this.getCellCenter(b);
        const [t0, t1] = origin === 'a' ? [0, grown]
            : origin === 'b' ? [1 - grown, 1]
            : [0.5 - grown / 2, 0.5 + grown / 2];

        this.ctx.beginPath();
        this.ctx.moveTo(ca.x + (cb.x - ca.x) * t0, ca.y + (cb.y - ca.y) * t0);
        this.ctx.lineTo(ca.x + (cb.x - ca.x) * t1, ca.y + (cb.y - ca.y) * t1);
        this.ctx.stroke();
    }

    private drawPathDots(game: KnightGame, grid: Grid, digitConnected: Set<string>, invalidCellKeys: Set<string>): void {
        const visited = game.getVisitedCells();
        const selected = game.getSelectedCell();
        const selectedKey = selected ? positionKey(selected) : null;

        if (visited.size === 0) return;

        for (const key of visited) {
            const [row, col] = key.split(',').map(Number);
            const pos = { row, col };
            const center = this.getCellCenter(pos);
            const isOnDigit = grid.hasDigitAt(pos);
            const isSelected = key === selectedKey;
            const connections = game.getConnectedCells(pos).length;
            const isEndpoint = connections <= 1;

            // Skip cells with no connections (no segments)
            if (connections === 0) {
                continue;
            }

            // Skip drawing dots on digit squares
            if (isOnDigit) {
                continue;
            }

            // Determine dot color: selected always teal, otherwise based on digit connectivity
            let color: string;
            if (isSelected) {
                color = this.knightConfig.selectedColor;
            } else if (digitConnected.has(key) && !invalidCellKeys.has(key)) {
                color = this.knightConfig.pathColor;
            } else {
                color = COLORS.amber;
            }

            // Draw normal center dot for non-digit squares
            this.ctx.fillStyle = color;
            this.ctx.beginPath();
            const radius = isSelected ? this.knightConfig.dotRadius + 2 : this.knightConfig.dotRadius;
            this.ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
            this.ctx.fill();
            this.ctx.strokeStyle = COLORS.background;
            this.ctx.lineWidth = 2;
            this.ctx.stroke();

            // Draw digit badge on endpoints that aren't on digit cells
            if (isEndpoint && !isOnDigit) {
                const endpointDigit = game.getEndpointDigit(pos);
                if (endpointDigit !== null) {
                    this.drawEndpointBadge(pos, endpointDigit);
                }
            }
        }
    }

    private drawEndpointBadge(pos: Position, digit: number): void {
        this.drawBadge(pos, 'NE', digit.toString(), COLORS.teal, 'sm', true);
    }

    // -- Vortex Gate Rendering ---------------------------------------------

    private drawTeleportLinks(game: KnightGame): void {
        if (!isVortexGame(game)) return;
        const teleportedPairs = (game as KnightVortex).getTeleportedPairs();
        if (teleportedPairs.size === 0) return;

        this.ctx.strokeStyle = COLORS.purple;
        this.ctx.lineWidth = 3;
        this.ctx.setLineDash([6, 4]);
        this.ctx.lineCap = 'round';

        for (const [entryKey, exitKey] of teleportedPairs) {
            const entryPos = parsePositionKey(entryKey);
            const exitPos = parsePositionKey(exitKey);
            const centerA = this.getCellCenter(entryPos);
            const centerB = this.getCellCenter(exitPos);

            this.ctx.beginPath();
            this.ctx.moveTo(centerA.x, centerA.y);
            this.ctx.lineTo(centerB.x, centerB.y);
            this.ctx.stroke();
        }

        this.ctx.setLineDash([]);
    }
}
